<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <CompanyAppLayout>
      <!-- Page Header -->
      <div class="pt-8 pb-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">Safety & Compliance</h1>
            <p class="text-slate-400">Manage safety protocols, compliance requirements, and incident reporting</p>
          </div>
          <div class="flex items-center space-x-3">
            <button @click="showIncidentModal = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
              Report Incident
            </button>
            <button @click="showTrainingModal = true" class="btn-secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
              </svg>
              Schedule Training
            </button>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-emerald-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Safety Score</p>
            <p class="text-3xl font-bold text-white">{{ safetyStats.safetyScore }}%</p>
            <p class="text-sm text-emerald-400">excellent</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-cyan-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-cyan-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Certified Crew</p>
            <p class="text-3xl font-bold text-white">{{ safetyStats.certifiedCrew }}</p>
            <p class="text-sm text-cyan-400">of {{ safetyStats.totalCrew }}</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-amber-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Days Since Incident</p>
            <p class="text-3xl font-bold text-white">{{ safetyStats.daysSinceIncident }}</p>
            <p class="text-sm text-amber-400">days</p>
          </div>
        </div>

        <div class="stat-card">
          <div class="flex items-center justify-center w-12 h-12 bg-purple-500/20 rounded-xl mb-4">
            <svg class="w-6 h-6 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-slate-400 mb-1">Pending Audits</p>
            <p class="text-3xl font-bold text-white">{{ safetyStats.pendingAudits }}</p>
            <p class="text-sm text-purple-400">due this month</p>
          </div>
        </div>
      </div>

      <!-- Safety Overview -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Compliance Checklist -->
        <div class="lg:col-span-2">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h2 class="text-xl font-semibold text-white mb-6">Compliance Checklist</h2>
            
            <div class="space-y-4">
              <div v-for="item in complianceChecklist" :key="item.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-3">
                  <div :class="[
                    'w-4 h-4 rounded-full border-2',
                    item.completed ? 'bg-emerald-500 border-emerald-500' : 'border-slate-500'
                  ]">
                    <svg v-if="item.completed" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                    </svg>
                  </div>
                  <div>
                    <h3 class="text-sm font-medium text-white">{{ item.title }}</h3>
                    <p class="text-xs text-slate-400">{{ item.description }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    item.priority === 'high' ? 'bg-red-500/20 text-red-400' :
                    item.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-emerald-500/20 text-emerald-400'
                  ]">
                    {{ item.priority }}
                  </span>
                  <button @click="toggleComplianceItem(item.id)" class="text-emerald-400 hover:text-emerald-300">
                    {{ item.completed ? 'Undo' : 'Complete' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Safety Alerts -->
        <div class="lg:col-span-1">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Safety Alerts</h3>
            
            <div class="space-y-3">
              <div v-for="alert in safetyAlerts" :key="alert.id" class="p-3 bg-slate-700/30 rounded-lg">
                <div class="flex items-start justify-between">
                  <div>
                    <h4 class="text-sm font-medium text-white">{{ alert.title }}</h4>
                    <p class="text-xs text-slate-400 mt-1">{{ alert.description }}</p>
                    <p class="text-xs text-slate-500 mt-2">{{ formatDate(alert.date) }}</p>
                  </div>
                  <span :class="[
                    'px-2 py-1 rounded-full text-xs font-medium',
                    alert.type === 'critical' ? 'bg-red-500/20 text-red-400' :
                    alert.type === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-blue-500/20 text-blue-400'
                  ]">
                    {{ alert.type }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Training Schedule -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Training Schedule</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Training</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Duration</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Instructor</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="training in trainingSchedule" :key="training.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-white">{{ training.title }}</div>
                    <div class="text-sm text-slate-400">{{ training.description }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(training.date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ training.duration }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ training.instructor }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      training.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                      training.status === 'scheduled' ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-amber-500/20 text-amber-400'
                    ]">
                      {{ training.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewTrainingDetails(training)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="rescheduleTraining(training.id)" class="text-cyan-400 hover:text-cyan-300">
                        Reschedule
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Incident Reports -->
      <div class="mt-8">
        <h2 class="text-xl font-semibold text-white mb-6">Recent Incident Reports</h2>
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead class="bg-slate-700/50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Date</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Type</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Location</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Severity</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Status</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-700/50">
                <tr v-for="incident in incidentReports" :key="incident.id" class="hover:bg-slate-700/30">
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ formatDate(incident.date) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-white">{{ incident.type }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-slate-300">{{ incident.location }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      incident.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                      incident.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    ]">
                      {{ incident.severity }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span :class="[
                      'px-2 py-1 rounded-full text-xs font-medium',
                      incident.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-400' :
                      incident.status === 'investigating' ? 'bg-cyan-500/20 text-cyan-400' :
                      'bg-amber-500/20 text-amber-400'
                    ]">
                      {{ incident.status }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div class="flex items-center space-x-2">
                      <button @click="viewIncidentDetails(incident)" class="text-emerald-400 hover:text-emerald-300">
                        View
                      </button>
                      <button @click="updateIncidentStatus(incident.id)" class="text-cyan-400 hover:text-cyan-300">
                        Update
                      </button>
                      <button
                        class="text-red-400 hover:text-red-300 disabled:opacity-50"
                        :disabled="deletingIncidentId === incident.id"
                        @click="deleteIncident(incident)"
                      >
                        {{ deletingIncidentId === incident.id ? 'Deleting…' : 'Delete' }}
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Report Incident Modal -->
      <div v-if="showIncidentModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Report Safety Incident</h3>
          
          <form @submit.prevent="submitIncidentReport" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Incident Type</label>
                <select v-model="incidentData.type" class="form-select w-full" required>
                  <option value="">Select Type</option>
                  <option value="injury">Injury</option>
                  <option value="equipment-failure">Equipment Failure</option>
                  <option value="near-miss">Near Miss</option>
                  <option value="environmental">Environmental</option>
                  <option value="security">Security</option>
                </select>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Severity</label>
                <select v-model="incidentData.severity" class="form-select w-full" required>
                  <option value="">Select Severity</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
              <input v-model="incidentData.location" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea v-model="incidentData.description" class="form-input w-full" rows="4" required></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showIncidentModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Submit Report
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Schedule Training Modal -->
      <div v-if="showTrainingModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
        <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl">
          <h3 class="text-xl font-semibold text-white mb-4">Schedule Safety Training</h3>
          
          <form @submit.prevent="scheduleTraining" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Training Title</label>
              <input v-model="trainingData.title" type="text" class="form-input w-full" required />
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Date</label>
                <input v-model="trainingData.date" type="date" class="form-input w-full" required />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Duration</label>
                <input v-model="trainingData.duration" type="text" class="form-input w-full" placeholder="2 hours" required />
              </div>
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Instructor</label>
              <input v-model="trainingData.instructor" type="text" class="form-input w-full" required />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea v-model="trainingData.description" class="form-input w-full" rows="3"></textarea>
            </div>
            
            <div class="flex items-center justify-end space-x-3 pt-4">
              <button type="button" @click="showTrainingModal = false" class="btn-secondary">
                Cancel
              </button>
              <button type="submit" class="btn-primary">
                Schedule Training
              </button>
            </div>
          </form>
        </div>
      </div>
    </CompanyAppLayout>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useUserStore } from '@/stores/userStore'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'

const userStore = useUserStore()

// Reactive data
const showIncidentModal = ref(false)
const showTrainingModal = ref(false)

const safetyStats = ref({
  safetyScore: 0,
  certifiedCrew: 0,
  totalCrew: 0,
  daysSinceIncident: 0,
  pendingAudits: 0
})

const complianceChecklist = ref([])
const safetyAlerts = ref([])
const trainingSchedule = ref([])
const incidentReports = ref([])
const deletingIncidentId = ref<number | null>(null)

const incidentData = ref({
  type: '',
  severity: '',
  location: '',
  description: ''
})

const trainingData = ref({
  title: '',
  date: '',
  duration: '',
  instructor: '',
  description: ''
})

// Methods
const fetchSafetyStats = async () => {
  try {
    const response = await fetch('/api/company/safety-stats', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      safetyStats.value = data
    }
  } catch (error) {
    console.error('Error fetching safety stats:', error)
  }
}

const fetchComplianceChecklist = async () => {
  try {
    const response = await fetch('/api/company/compliance-checklist', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      complianceChecklist.value = data.checklist || []
    }
  } catch (error) {
    console.error('Error fetching compliance checklist:', error)
  }
}

const fetchSafetyAlerts = async () => {
  try {
    const response = await fetch('/api/company/safety-alerts', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      safetyAlerts.value = data.alerts || []
    }
  } catch (error) {
    console.error('Error fetching safety alerts:', error)
  }
}

const fetchTrainingSchedule = async () => {
  try {
    const response = await fetch('/api/company/training-schedule', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      trainingSchedule.value = data.schedule || []
    }
  } catch (error) {
    console.error('Error fetching training schedule:', error)
  }
}

const fetchIncidentReports = async () => {
  try {
    const response = await fetch('/api/company/incident-reports', {
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      incidentReports.value = data.incidents || []
    }
  } catch (error) {
    console.error('Error fetching incident reports:', error)
  }
}

const toggleComplianceItem = async (itemId: number) => {
  try {
    const response = await fetch('/api/company/toggle-compliance', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ itemId })
    })
    
    if (response.ok) {
      await fetchComplianceChecklist()
    }
  } catch (error) {
    console.error('Error toggling compliance item:', error)
  }
}

const submitIncidentReport = async () => {
  try {
    const response = await fetch('/api/company/submit-incident', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(incidentData.value)
    })
    
    if (response.ok) {
      showIncidentModal.value = false
      incidentData.value = { type: '', severity: '', location: '', description: '' }
      await fetchIncidentReports()
      await fetchSafetyStats()
    }
  } catch (error) {
    console.error('Error submitting incident report:', error)
  }
}

const scheduleTraining = async () => {
  try {
    const response = await fetch('/api/company/schedule-training', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(trainingData.value)
    })
    
    if (response.ok) {
      showTrainingModal.value = false
      trainingData.value = { title: '', date: '', duration: '', instructor: '', description: '' }
      await fetchTrainingSchedule()
    }
  } catch (error) {
    console.error('Error scheduling training:', error)
  }
}

const viewTrainingDetails = (training: any) => {
  // Navigate to training details page
}

const rescheduleTraining = (trainingId: number) => {
  // Open reschedule modal
}

const viewIncidentDetails = (incident: any) => {
  // Navigate to incident details page
}

const deleteIncident = async (incident: any) => {
  if (!window.confirm(`Delete incident “${incident.type || 'this report'}”? This cannot be undone.`)) return
  deletingIncidentId.value = incident.id
  try {
    const response = await fetch(`/api/company/incident-reports/${incident.id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${userStore.authToken}`,
        'Content-Type': 'application/json',
      },
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`)
    incidentReports.value = incidentReports.value.filter((item: any) => item.id !== incident.id)
    await fetchSafetyStats()
  } catch (error) {
    console.error('Error deleting incident:', error)
  } finally {
    deletingIncidentId.value = null
  }
}

const updateIncidentStatus = (incidentId: number) => {
  // Open update modal
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString()
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    fetchSafetyStats(),
    fetchComplianceChecklist(),
    fetchSafetyAlerts(),
    fetchTrainingSchedule(),
    fetchIncidentReports()
  ])
})
</script>

<style scoped>
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}

.btn-primary {
  @apply px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center;
}

.btn-secondary {
  @apply px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 font-medium rounded-lg transition-colors flex items-center;
}

.form-input, .form-select {
  @apply bg-slate-700/50 border border-slate-600/50 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent;
}

.form-select {
  @apply min-w-[120px];
}
</style>
